import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { storageHelper } from 'src/app/misc/storage.misc';
import { jwtTokenIdentifier, userIdentifier } from 'src/app/misc/utilities.misc';
import { AuthService } from 'src/app/services/auth.service';
import { SweetAlertService } from 'src/app/services/sweetalert.service';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss']
})
export class SigninComponent implements OnInit {
  signinForm!: FormGroup;
  isLoading = false;
  submitted = false;
  showPassword = false;
  rememberMe = false;
  loginError = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private sweetAlertService: SweetAlertService,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.checkRememberedUser();
    this.handleActivationRequiredNotice();
    this.listenToAuthEvents();
  }

  initForm(): void {
    this.signinForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  checkRememberedUser(): void {
    // Vérifier si l'utilisateur a coché "Se souvenir de moi"
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      this.signinForm.patchValue({
        email: rememberedEmail,
        rememberMe: true
      });
      this.rememberMe = true;
    }
  }

  handleActivationRequiredNotice(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    if (queryParams.get('activation_required') !== '1') {
      return;
    }

    const email = queryParams.get('email') || '';
    if (email) {
      this.signinForm.patchValue({ email });
    }

    this.sweetAlertService.toastWarning(
      'Activation du compte requise',
      8000,
      email
        ? `Un email d'activation a ete envoye a ${email}. Activez votre compte avant de vous connecter.`
        : "Un email d'activation vous a ete envoye. Activez votre compte avant de vous connecter."
    );

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { activation_required: null, email: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  listenToAuthEvents() {
    this.authService.authSuccessEvent.subscribe((data) => {

      this.submitted = false;

      const userRole = (data?.user?.role || '').toString().toLowerCase();
      if (userRole !== 'user') {
        storageHelper.local.remove(`${jwtTokenIdentifier}`);
        storageHelper.local.remove(`${userIdentifier}`);
        this.sweetAlertService.toastWarning(
          'Acces refuse',
          5000,
          'Votre compte n est pas autorise a acceder au dashboard trader.'
        );
        this.router.navigateByUrl('/auth/signin');
        return;
      }

      storageHelper.local.store(`${jwtTokenIdentifier}`, data.access);
      storageHelper.local.store(`${userIdentifier}`, data.user);

      const defaultUrl = userRole === 'user' ? '/dashboard' : '/';
      const returnUrl =
        this.router.routerState.snapshot.root.queryParams['returnUrl'] || defaultUrl;

      const targetUrl = userRole === 'user' ? returnUrl : '/';

      this.router.navigateByUrl(targetUrl).then(() => {
        this.handlePendingAction();
      });

    });

    this.authService.authFailureEvent.subscribe((error: HttpErrorResponse) => {
      this.submitted = false;
      this.sweetAlertService.toastError(
        'Erreur de connexion !',
        5000,
        'Veuillez vérifier vos accès et réessayer'
      );
    });
  }


  handlePendingAction() {
    const pending = localStorage.getItem('pendingAction');

    if (!pending) return;

    const action = JSON.parse(pending);

    // petit délai pour laisser Angular rendre la page cible et enregistrer
    // ses ecouteurs (ngOnInit) avant qu'on ne redeclenche l'action.
    if (action.type === 'OPEN_PAIEMENT_MODAL' || action.type === 'OPEN_COMMENT_MODAL') {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(action.type, {
            detail: action.payload
          })
        );
      }, 300);
    }

    localStorage.removeItem('pendingAction');
  }



  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    if (this.signinForm.valid) {
      this.isLoading = true;
      this.loginError = '';

      const { email, password, rememberMe } = this.signinForm.value;

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }


      this.isLoading = true;
      this.authService.login(email, password).subscribe({
        next: (data: any) => {
          this.isLoading = false;
          this.authService.onAuthSuccess(data);
          this.authService.setAuthUser(data);
          this.sweetAlertService.toastSuccess('Connexion réussie!', 3000);
          this.resetForm();
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.loginError = error.error.detail;
          this.authService.onAuthFailure(error);
          this.sweetAlertService.toastError('Erreur !', 5000, (error.error.detail || error.error.error) || 'Le service est temporairement indisponible');
        },
      });
    } else {
      Object.keys(this.signinForm.controls).forEach(key => {
        this.signinForm.get(key)?.markAsTouched();
      });
    }
  }

  resetForm() {
    this.signinForm.reset();
    Object.keys(this.signinForm.controls).forEach(c => {
      this.signinForm.controls[c].setErrors(null);
    });
  }

  navigateToSignup(): void {
    this.router.navigate(['/auth/signup']);
  }

  navigateToForgotPassword(): void {
    this.router.navigate(['/auth/forgot-password']);
  }

  getFieldError(fieldName: string): string {
    const field = this.signinForm.get(fieldName);

    if (field?.hasError('required')) {
      return 'Ce champ est requis';
    }
    if (field?.hasError('email')) {
      return 'Email invalide';
    }
    if (field?.hasError('minlength')) {
      const minLength = field.errors?.['minlength'].requiredLength;
      return `Minimum ${minLength} caractères requis`;
    }

    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.signinForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }
}
